import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToastType, UserProfile } from '../types';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import RoddoSectionCard from './layout/RoddoSectionCard';
import { RODDO_ADMOB, canUseNativeAdMob } from '../constants/adMob';
import {
  getRoddoAdPrivacyState,
  refreshRoddoAdPrivacy,
  showRoddoAdPrivacyOptions,
} from '../services/adMob';
import {
  FISHIQ_DATA_PROMISE_VERSION,
  LEGAL_SCHEMA_VERSION,
  PRIVACY_POLICY_PARAGRAPHS,
  PRIVACY_POLICY_TITLE,
  PRIVACY_VERSION,
  TERMS_OF_SERVICE_PARAGRAPHS,
  TERMS_OF_SERVICE_TITLE,
  TOS_VERSION,
  DMCA_AGENT_NAME,
  DMCA_AGENT_ORGANIZATION,
  DMCA_AGENT_EMAIL,
  DMCA_AGENT_PHONE,
  DMCA_AGENT_ADDRESS,
} from '../legal/legalText';

type Props = {
  userProfile: UserProfile;
  onBack: () => void;
  addToast: (msg: string, type?: ToastType) => void;
  onUpdateProfile: (nextProfile: UserProfile) => Promise<void> | void;
  onOpenIntro?: () => void;
  isDarkMode?: boolean;
  onOpenTos?: () => void;
  onOpenPrivacy?: () => void;
};

type Tone = 'neutral' | 'good' | 'warn';

function toLegalTimestampMs(value: any): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (value && typeof value.toMillis === 'function') {
    try {
      const millis = Number(value.toMillis());
      return Number.isFinite(millis) && millis > 0 ? millis : null;
    } catch {}
  }

  if (value && typeof value.seconds === 'number') {
    const millis =
      Number(value.seconds) * 1000 +
      Math.floor(Number(value.nanoseconds || 0) / 1000000);
    return Number.isFinite(millis) && millis > 0 ? millis : null;
  }

  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isFinite(millis) && millis > 0 ? millis : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const millis = Date.parse(value);
    return Number.isFinite(millis) && millis > 0 ? millis : null;
  }

  return null;
}

function formatDate(ts?: any) {
  const millis = toLegalTimestampMs(ts);
  if (!millis) return 'Not recorded';
  try {
    return new Date(millis).toLocaleString();
  } catch {
    return 'Not recorded';
  }
}

function StatusBadge({ children, tone = 'neutral', dark }: { children: React.ReactNode; tone?: Tone; dark: boolean }) {
  const palette: Record<Tone, React.CSSProperties> = {
    neutral: {
      background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.74)',
      borderColor: dark ? 'rgba(255,255,255,0.12)' : 'rgba(86,70,110,0.14)',
      color: dark ? 'rgba(255,255,255,0.72)' : 'rgba(62,51,84,0.72)',
    },
    good: {
      background: dark ? 'rgba(16,185,129,0.16)' : 'rgba(209,250,229,0.88)',
      borderColor: dark ? 'rgba(16,185,129,0.34)' : 'rgba(16,185,129,0.30)',
      color: dark ? '#a7f3d0' : '#047857',
    },
    warn: {
      background: dark ? 'rgba(245,158,11,0.16)' : 'rgba(254,243,199,0.90)',
      borderColor: dark ? 'rgba(245,158,11,0.34)' : 'rgba(245,158,11,0.30)',
      color: dark ? '#fde68a' : '#92400e',
    },
  };

  return (
    <span
      className="inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.08em]"
      style={palette[tone]}
    >
      {children}
    </span>
  );
}

function ActionButton({ children, onClick, disabled = false, dark }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean; dark: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-9 items-center justify-center rounded-[12px] px-4 py-2 text-[12px] font-black transition duration-150 ease-out hover:-translate-y-0.5 hover:brightness-110 active:translate-y-[1px] disabled:pointer-events-none disabled:opacity-45"
      style={{
        background: dark ? '#7057E8' : '#2F80ED',
        border: dark ? '1px solid rgba(169,151,240,0.28)' : '1px solid rgba(47,128,237,0.24)',
        color: '#FFFFFF',
        boxShadow: dark ? '0 6px 16px rgba(112,87,232,0.18)' : '0 5px 14px rgba(47,128,237,0.16)',
      }}
    >
      {children}
    </button>
  );
}

function SettingsRow({
  title,
  description,
  meta,
  action,
  dark,
}: {
  title: string;
  description: string;
  meta?: React.ReactNode;
  action?: React.ReactNode;
  dark: boolean;
}) {
  const ui = getRoddoUiTokens(dark);

  return (
    <div className="grid gap-3 py-4 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="min-w-0">
        <div className="text-[14px] font-black leading-5" style={{ color: ui.textPrimary }}>
          {title}
        </div>
        <div className="mt-1 max-w-[34rem] text-[12px] leading-5" style={{ color: ui.textSecondary }}>
          {description}
        </div>
        {meta ? (
          <div className="mt-2 text-[11px] leading-4" style={{ color: ui.textMuted }}>
            {meta}
          </div>
        ) : null}
      </div>
      {action ? <div className="flex flex-wrap items-center gap-2 sm:justify-end">{action}</div> : null}
    </div>
  );
}

function LegalDocumentText({ paragraphs }: { paragraphs: string[] }) {
  return (
    <div className="space-y-3">
      {paragraphs.map((p, index) => (
        <p key={index} className="whitespace-pre-line text-sm leading-6 text-[#4F5A63] dark:text-[#AEB8C9]">
          {p}
        </p>
      ))}
    </div>
  );
}

function DocModal({ title, children, onClose, dark }: { title: string; children: React.ReactNode; onClose: () => void; dark: boolean }) {
  return (
    <div className="fixed inset-0 z-[1200] flex items-end justify-center bg-black/55 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-[max(12px,env(safe-area-inset-top))] backdrop-blur-sm sm:items-center" role="dialog" aria-modal="true">
      <div
        className="flex w-full max-w-2xl flex-col overflow-hidden rounded-[20px] border shadow-xl"
        style={{
          background: dark ? '#121A2B' : '#FFFCF7',
          borderColor: dark ? 'rgba(180,195,220,0.14)' : 'rgba(74,58,42,0.14)',
        }}
      >
        <div className="flex items-center justify-between gap-3 border-b px-5 py-4">
          <div className="text-base font-black">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border text-lg font-black transition hover:-translate-y-0.5 active:translate-y-[1px]"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 [-webkit-overflow-scrolling:touch]" style={{ maxHeight: 'calc(var(--roddo-visual-vh, 100dvh) - max(24px, env(safe-area-inset-top)) - max(24px, env(safe-area-inset-bottom)) - 74px)' }}>{children}</div>
      </div>
    </div>
  );
}

export default function LegalSettingsView({
  userProfile,
  onBack,
  addToast,
  onUpdateProfile,
  onOpenIntro,
  isDarkMode,
  onOpenTos,
  onOpenPrivacy,
}: Props) {
  const { t } = useTranslation();
  const dark = typeof isDarkMode === 'boolean' ? isDarkMode : resolveRoddoDarkMode();
  const ui = getRoddoUiTokens(dark);
  const legal = ((userProfile as any)?.legal ?? {}) as any;

  const legalSchemaCurrent = legal?.schemaVersion === LEGAL_SCHEMA_VERSION;
  const termsAccepted =
    legalSchemaCurrent &&
    legal?.tos?.version === TOS_VERSION &&
    Boolean(legal?.tos?.acceptedAt);
  const privacyAccepted =
    legalSchemaCurrent &&
    legal?.privacy?.version === PRIVACY_VERSION &&
    Boolean(legal?.privacy?.acceptedAt);
  const tosAcceptedAt = formatDate(legal?.tos?.acceptedAt);
  const privacyAcceptedAt = formatDate(legal?.privacy?.acceptedAt);

  const initialFishIQEnabled = useMemo(() => {
    const direct = (userProfile as any)?.fishiqContributionEnabled;
    if (typeof direct === 'boolean') return direct;
    const consent = legal?.consents?.fishiqContribution?.enabled;
    if (typeof consent === 'boolean') return consent;
    return false;
  }, [legal?.consents?.fishiqContribution?.enabled, userProfile]);

  const [fishIQEnabled, setFishIQEnabled] = useState(initialFishIQEnabled);
  const [isSaving, setIsSaving] = useState(false);
  const [openDoc, setOpenDoc] = useState<null | 'tos' | 'privacy'>(null);
  const [adPrivacyRequired, setAdPrivacyRequired] = useState(false);
  const [adPrivacyBusy, setAdPrivacyBusy] = useState(false);

  useEffect(() => {
    if (!RODDO_ADMOB.enabled || !canUseNativeAdMob()) return;
    let cancelled = false;
    void refreshRoddoAdPrivacy().then((state) => {
      if (!cancelled) setAdPrivacyRequired(state.privacyOptionsRequired);
    });
    return () => { cancelled = true; };
  }, []);

  const openAdPrivacyOptions = async () => {
    if (adPrivacyBusy) return;
    setAdPrivacyBusy(true);
    try {
      const opened = await showRoddoAdPrivacyOptions();
      const state = getRoddoAdPrivacyState();
      setAdPrivacyRequired(state.privacyOptionsRequired);
      if (!opened) addToast(t('legal.adPrivacyUnavailable', 'Ad privacy options are unavailable right now.'), 'error');
    } finally {
      setAdPrivacyBusy(false);
    }
  };

  const changed = fishIQEnabled !== initialFishIQEnabled;
  const ageBracket =
    (userProfile as any)?.ageEligibility?.bracket ??
    (userProfile as any)?.ageBracket ??
    legal?.minor?.ageBracket ??
    'UNKNOWN';
  const isMinor = ageBracket === '13_15' || ageBracket === '16_17';
  const parentalRequired = Boolean(
    legal?.minor?.parentalConsent?.required ?? (userProfile as any)?.parentalConsentRequired,
  );
  const parentalReceived = Boolean(
    legal?.minor?.parentalConsent?.received ?? (userProfile as any)?.parentalConsentReceivedAt,
  );
  const parentalReceivedAt = formatDate(
    legal?.minor?.parentalConsent?.receivedAt ?? (userProfile as any)?.parentalConsentReceivedAt,
  );
  const showAgeSection = ageBracket !== 'UNKNOWN' || parentalRequired;

  const saveFishIQ = async () => {
    if (!changed || isSaving) return;
    setIsSaving(true);
    try {
      const now = Date.now();
      const nextProfile = {
        ...(userProfile as any),
        fishiqContributionEnabled: fishIQEnabled,
        legal: {
          ...legal,
          schemaVersion: LEGAL_SCHEMA_VERSION,
          consents: {
            ...(legal?.consents ?? {}),
            fishiqContribution: {
              ...(legal?.consents?.fishiqContribution ?? {}),
              enabled: fishIQEnabled,
              version: FISHIQ_DATA_PROMISE_VERSION,
              acceptedAt: fishIQEnabled
                ? legal?.consents?.fishiqContribution?.acceptedAt ?? now
                : null,
              declinedAt: fishIQEnabled ? null : now,
              updatedAt: now,
            },
          },
        },
      } as UserProfile;

      await onUpdateProfile(nextProfile);
      addToast(fishIQEnabled ? t('legal.contributionEnabled', 'Contribution enabled') : t('legal.contributionDisabled', 'Contribution disabled'), 'success');
    } catch (error) {
      console.error(error);
      addToast(t('legal.preferenceSaveError', 'Could not save fishing-data preference.'), 'error');
      setFishIQEnabled(initialFishIQEnabled);
    } finally {
      setIsSaving(false);
    }
  };

  const openTerms = () => {
    if (onOpenTos) onOpenTos();
    else setOpenDoc('tos');
  };

  const openPrivacy = () => {
    if (onOpenPrivacy) onOpenPrivacy();
    else setOpenDoc('privacy');
  };

  return (
    <div className="pb-8" data-roddo-theme={dark ? 'dark' : 'light'}>
      <RoddoSectionCard isDarkMode={dark} className="overflow-hidden !p-0">
        <div
          className="relative px-5 pb-5 pt-5 sm:px-6 sm:pt-6"
          style={{
            background: dark ? '#121A2B' : '#FFFCF7',
            borderBottom: `1px solid ${ui.border}`,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-lg font-black transition duration-150 ease-out hover:-translate-y-0.5 hover:brightness-110 active:translate-y-[1px]"
            style={{
              background: dark ? 'rgba(11,9,31,0.76)' : 'rgba(255,255,255,0.86)',
              border: `1px solid ${ui.borderStrong}`,
              color: ui.textPrimary,
              boxShadow: dark ? '0 4px 12px rgba(0,0,0,0.18)' : '0 4px 10px rgba(68,57,45,0.06)',
            }}
          >
            ‹
          </button>

          <div className="pr-12">
            <div
              className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em]"
              style={{
                background: dark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.66)',
                borderColor: ui.border,
                color: ui.textMuted,
              }}
            >
              {t('legal.trustCenter', 'Trust center')}
              <span aria-hidden="true">📜</span>
            </div>
            <h1 className="text-2xl font-black leading-tight" style={{ color: ui.textPrimary }}>
              {t('legal.legalPrivacy', 'Legal & Privacy')}
            </h1>
            <p className="mt-2 max-w-[34rem] text-[13px] leading-5" style={{ color: ui.textSecondary }}>
              {t('legal.legalPrivacyCopy', 'Review Roddo documents, consent status, onboarding, and fishing-data preferences.')}
            </p>
          </div>
        </div>

        <div className="px-5 py-1 sm:px-6">
          <section className="py-4">
            <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textMuted }}>
              {t('legal.documents', 'Documents')}
            </div>

            <SettingsRow
              dark={dark}
              title={t('legal.terms', 'Terms of Service')}
              description={t('legal.termsCopy', 'Rules for using Roddo and community features.')}
              meta={<>{t('legal.version', 'Version')} {TOS_VERSION} · {t('legal.accepted', 'Accepted')} {tosAcceptedAt}</>}
              action={
                <>
                  <StatusBadge tone={termsAccepted ? 'good' : 'warn'} dark={dark}>
                    {termsAccepted ? t('common.accepted', 'Accepted') : t('common.missing', 'Missing')}
                  </StatusBadge>
                  <ActionButton onClick={openTerms} dark={dark}>{t('common.view', 'View')}</ActionButton>
                </>
              }
            />

            <div style={{ borderTop: `1px solid ${ui.border}` }} />

            <SettingsRow
              dark={dark}
              title={t('legal.privacy', 'Privacy Policy')}
              description={t('legal.privacyCopy', 'How Roddo collects, uses, and protects account, location, logs, and content data.')}
              meta={<>{t('legal.version', 'Version')} {PRIVACY_VERSION} · {t('legal.accepted', 'Accepted')} {privacyAcceptedAt}</>}
              action={
                <>
                  <StatusBadge tone={privacyAccepted ? 'good' : 'warn'} dark={dark}>
                    {privacyAccepted ? t('common.accepted', 'Accepted') : t('common.missing', 'Missing')}
                  </StatusBadge>
                  <ActionButton onClick={openPrivacy} dark={dark}>{t('common.view', 'View')}</ActionButton>
                </>
              }
            />

            <div style={{ borderTop: `1px solid ${ui.border}` }} />

            <SettingsRow
              dark={dark}
              title="Copyright / DMCA"
              description="Copyright infringement notices may be sent to Roddo's designated copyright agent."
              meta={(
                <>
                  {DMCA_AGENT_NAME} · {DMCA_AGENT_ORGANIZATION}<br />
                  {DMCA_AGENT_EMAIL} · {DMCA_AGENT_PHONE}<br />
                  {DMCA_AGENT_ADDRESS}
                </>
              )}
              action={(
                <a
                  href={`mailto:${DMCA_AGENT_EMAIL}`}
                  className="inline-flex min-h-9 items-center justify-center rounded-[12px] px-4 py-2 text-[12px] font-black text-white transition hover:-translate-y-0.5 hover:brightness-110 active:translate-y-[1px]"
                  style={{ background: dark ? '#7057E8' : '#2F80ED' }}
                >
                  Email agent
                </a>
              )}
            />
          </section>

          <section className="py-4" style={{ borderTop: `1px solid ${ui.border}` }}>
            <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textMuted }}>
              {t('legal.dataPreference', 'Data preference')}
            </div>

            <SettingsRow
              dark={dark}
              title={t('legal.fishiqContribution', 'Fishing-data contribution')}
              description={t('legal.fishiqContributionCopy', 'Allow eligible logs and observations to improve Roddo in aggregated, anonymized form. Personal profile details are not sold.')}
              meta={fishIQEnabled ? t('legal.contributionEnabledSentence', 'Contribution is currently enabled.') : t('legal.contributionDisabledSentence', 'Contribution is currently disabled.')}
              action={
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => setFishIQEnabled((value) => !value)}
                  className="relative h-8 w-14 rounded-full border transition duration-200 ease-out hover:brightness-110 active:scale-95 disabled:opacity-60"
                  style={{
                    background: fishIQEnabled ? '#22c55e' : dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
                    borderColor: fishIQEnabled ? 'rgba(34,197,94,0.55)' : ui.borderStrong,
                  }}
                  aria-label={t('legal.toggleFishiqContribution', 'Toggle fishing-data contribution')}
                >
                  <span
                    className="absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all duration-200 ease-out"
                    style={{ left: fishIQEnabled ? '1.65rem' : '0.25rem' }}
                  />
                </button>
              }
            />

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <StatusBadge tone={fishIQEnabled ? 'good' : 'neutral'} dark={dark}>
                {fishIQEnabled ? t('legal.contributionEnabled', 'Contribution enabled') : t('legal.contributionDisabled', 'Contribution disabled')}
              </StatusBadge>
              <ActionButton onClick={saveFishIQ} disabled={!changed || isSaving} dark={dark}>
                {isSaving ? t('common.saving', 'Saving…') : changed ? t('common.saveChanges', 'Save changes') : t('common.saved', 'Saved')}
              </ActionButton>
            </div>
          </section>

          {RODDO_ADMOB.enabled && canUseNativeAdMob() && adPrivacyRequired ? (
            <section className="py-4" style={{ borderTop: `1px solid ${ui.border}` }}>
              <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textMuted }}>
                {t('legal.adPrivacy', 'Advertising privacy')}
              </div>
              <SettingsRow
                dark={dark}
                title={t('legal.adPrivacyOptions', 'Ad privacy options')}
                description={t('legal.adPrivacyOptionsCopy', 'Review or update advertising privacy choices provided by Google when required for your region.')}
                meta={t('legal.adPrivacyMeta', 'Roddo does not send private fishing spots or catch coordinates for ad targeting.')}
                action={
                  <ActionButton onClick={openAdPrivacyOptions} disabled={adPrivacyBusy} dark={dark}>
                    {adPrivacyBusy ? t('common.loading', 'Loading…') : t('common.manage', 'Manage')}
                  </ActionButton>
                }
              />
            </section>
          ) : null}

          {onOpenIntro ? (
            <section className="py-4" style={{ borderTop: `1px solid ${ui.border}` }}>
              <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textMuted }}>
                {t('legal.onboarding', 'Onboarding')}
              </div>
              <SettingsRow
                dark={dark}
                title={t('legal.onboardingWalkthrough', 'Onboarding walkthrough')}
                description={t('legal.onboardingCopy', 'Replay the guided app tour for a quick refresher on Roddo basics.')}
                meta={t('legal.onboardingMeta', 'This does not reset your account, profile, logs, or legal acceptance.')}
                action={<ActionButton onClick={onOpenIntro} dark={dark}>{t('legal.openTour', 'Replay tour')}</ActionButton>}
              />
            </section>
          ) : null}

          {showAgeSection ? (
            <section className="py-4" style={{ borderTop: `1px solid ${ui.border}` }}>
              <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textMuted }}>
                {t('legal.ageConsent', 'Age & consent')}
              </div>
              <SettingsRow
                dark={dark}
                title={t('legal.ageStatus', 'Age status')}
                description={isMinor ? t('legal.minorReadonly', 'Minor account settings are currently read-only.') : t('legal.noMinorRestrictions', 'No minor account restrictions are active.')}
                meta={
                  parentalRequired
                    ? `${t('legal.parentalConsent', 'Parental consent')}: ${parentalReceived ? t('legal.parentalReceived', 'received {{date}}', { date: parentalReceivedAt }) : t('legal.parentalNotReceived', 'not received')}`
                    : t('legal.parentalNotRequired', 'Parental consent is not required.')
                }
                action={
                  <StatusBadge tone={parentalRequired && !parentalReceived ? 'warn' : 'neutral'} dark={dark}>
                    {ageBracket}
                  </StatusBadge>
                }
              />
            </section>
          ) : null}
        </div>
      </RoddoSectionCard>

      {openDoc === 'tos' ? (
        <DocModal title={TERMS_OF_SERVICE_TITLE} onClose={() => setOpenDoc(null)} dark={dark}>
          <LegalDocumentText paragraphs={TERMS_OF_SERVICE_PARAGRAPHS} />
        </DocModal>
      ) : null}

      {openDoc === 'privacy' ? (
        <DocModal title={PRIVACY_POLICY_TITLE} onClose={() => setOpenDoc(null)} dark={dark}>
          <LegalDocumentText paragraphs={PRIVACY_POLICY_PARAGRAPHS} />
        </DocModal>
      ) : null}
    </div>
  );
}
